import { gql, useQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { CalendarIcon, InfoCircleIcon } from '@orn/shared-web/icons';
import {
  formatTransactionAmount,
  transactionCaption,
  transactionDisplayName,
  transactionFooter,
} from '@orn/shared-web/lib/transaction/transactionDisplay';
import formatAccountLabel from '@orn/shared-web/utils/account/formatAccountLabel';
import { useParams } from 'react-router-dom';

import Entity from '../../components/entity';
import { entityLogoSrc } from '../../components/entity/EntityAvatar/EntityAvatar';
import RecordAlertBanner from '../../components/entity/RecordAlertBanner';
import RecordPane, { useCloseRecord } from '../../components/entity/RecordPane';
import AttachmentsSection from '../../components/records/AttachmentsSection';
import MerchantSpendingSection from '../../components/records/MerchantSpendingSection';
import RecentTransactionsSection from '../../components/records/RecentTransactionsSection';
import RecurringStreamSection, { RecurringStreamSectionBase } from '../../components/records/RecurringStreamSection';
import { JsonDebugRow } from '../../components/records/rows';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';
import { usePathWithDateRange } from '../../providers/DateRangeProvider';
import { entityHref } from '../../utils/entityDestination';
import { formatCalendarDate } from '../../utils/formatting';

import { resolveTransactionPageStatus } from './transactionPage';

import type { GetTransactionPageQuery, GetTransactionPageQueryVariables } from './__generated__/TransactionPane.gql';
import type { IRecordHeroMetaCell } from '../../components/entity/RecordPane';

const GET_TRANSACTION_PAGE = gql`
  query GetTransactionPage($id: ID!, $includeRawJson: Boolean!) {
    getTransaction(id: $id) {
      id
      amount
      currencyCode
      date
      description
      rawDescription
      isPending
      isUnverified
      rawJson @include(if: $includeRawJson)
      merchant {
        id
        ...EntityAvatar
      }
      financialAccount {
        id
        name
        mask
        ...EntityAvatar
      }
      recurringTransaction {
        id
        rawJson @include(if: $includeRawJson)
        ...RecurringStreamSection
      }
      bill {
        id
        ...AttachmentsSectionBill
      }
      workItem {
        ...RecordAlertBanner
      }
    }
  }
  ${Entity.Avatar.Fragment}
  ${RecurringStreamSectionBase.Fragment}
  ${AttachmentsSection.Fragment}
  ${RecordAlertBanner.Fragment}
`;

export type TransactionPageRecord = GetTransactionPageQuery['getTransaction'];

interface ITransactionPaneProps {
  /** Stories pass this directly; production rendering falls back to the route param. */
  transactionId?: string;
}

/**
 * The transaction record page at `/transactions/:transactionId`: the record
 * shell over one `GetTransactionPage` read. The hero carries the merchant (or
 * the initial of the description), the signed amount, a "Pending" or
 * "Manual bill payment" caption, and the Date and Account cells — the merchant
 * and the account each link to their own page. A row the household recorded
 * itself with no account reads "None" in the Account cell, with a tooltip
 * saying why. The body is the record's alert
 * banner and the sections that have data — the stream's details when the
 * transaction is on a stream, else Spending at the merchant; recent transactions at the
 * merchant; the bill's attachments — then the raw bank description as the
 * footer when it says more than the name.
 *
 * Closing goes back in history, or to the record's account page on a first
 * entry. The raw JSON is selected, and its rows drawn, only when
 * `FeatureGate.DebugTransactionDetails` is on.
 */
const TransactionPane = ({ transactionId: transactionIdProp }: ITransactionPaneProps = {}) => {
  const params = useParams();
  const transactionId = transactionIdProp ?? params.transactionId ?? '';
  const showDebugJson = useFeatureGate(FeatureGate.DebugTransactionDetails);

  const { data, loading, error } = useQuery<GetTransactionPageQuery, GetTransactionPageQueryVariables>(
    GET_TRANSACTION_PAGE,
    { variables: { id: transactionId, includeRawJson: showDebugJson }, skip: !transactionId }
  );
  const transaction = data?.getTransaction;
  const addDateRangeToPath = usePathWithDateRange();
  const closeRecord = useCloseRecord(
    transaction?.financialAccount ? PATHS.ACCOUNT(transaction.financialAccount.id) : PATHS.ACCOUNTS
  );

  const status = resolveTransactionPageStatus({ loading, error, hasTransaction: Boolean(transaction) });

  if (!transaction) {
    return (
      <RecordPane
        status={status}
        onClose={closeRecord}
        notFoundMessage={transactionId ? "This transaction isn't available." : 'No transaction was specified.'}
      />
    );
  }

  const displayName = transactionDisplayName(transaction);
  const hasDebugJson = Boolean(transaction.rawJson || transaction.recurringTransaction?.rawJson);

  return (
    <RecordPane
      status={status}
      onClose={closeRecord}
      hero={
        <RecordPane.Hero
          logoSrc={transaction.merchant ? entityLogoSrc(transaction.merchant) : null}
          name={displayName}
          to={
            transaction.merchant
              ? addDateRangeToPath(entityHref({ type: 'merchant', id: transaction.merchant.id }))
              : undefined
          }
          amount={formatTransactionAmount(transaction.amount, transaction.currencyCode)}
          caption={transactionCaption(transaction) ?? undefined}
          meta={buildHeroMeta(transaction, addDateRangeToPath)}
          onClose={closeRecord}
        />
      }
      footer={transactionFooter(transaction.rawDescription, displayName)}
    >
      <RecordAlertBanner workItem={transaction.workItem} />
      {transaction.recurringTransaction ? (
        <RecurringStreamSection recurringTransaction={transaction.recurringTransaction} amount={transaction.amount} />
      ) : (
        <MerchantSpendingSection merchantId={transaction.merchant?.id} amount={transaction.amount} />
      )}
      <RecentTransactionsSection merchantId={transaction.merchant?.id} excludeTransactionId={transaction.id} />
      <AttachmentsSection bill={transaction.bill} />
      {showDebugJson && hasDebugJson && (
        <RecordPane.Section title="Debug">
          <JsonDebugRow label="Transaction JSON" rawJson={transaction.rawJson} />
          {transaction.recurringTransaction && (
            <JsonDebugRow label="Recurring transaction JSON" rawJson={transaction.recurringTransaction.rawJson} />
          )}
        </RecordPane.Section>
      )}
    </RecordPane>
  );
};

/**
 * The Date cell, then the Account cell — the bank's square avatar, linked to
 * the account's page — when the record has an account. A manually recorded row
 * with no account reads "None" there, with a tooltip saying why.
 */
function buildHeroMeta(
  transaction: TransactionPageRecord,
  addDateRangeToPath: (path: string) => string
): IRecordHeroMetaCell[] {
  const cells: IRecordHeroMetaCell[] = [
    { label: 'Date', value: formatCalendarDate(transaction.date, 'medium'), icon: <CalendarIcon /> },
  ];
  const { financialAccount, isUnverified } = transaction;
  if (financialAccount) {
    cells.push({
      label: 'Account',
      value: formatAccountLabel(financialAccount),
      leading: <Entity.Avatar entity={financialAccount} size="sm" />,
      to: addDateRangeToPath(entityHref({ type: 'financialAccount', id: financialAccount.id })),
    });
  } else if (isUnverified) {
    cells.push({
      label: 'Account',
      value: 'None',
      icon: <InfoCircleIcon />,
      tooltip: 'This transaction was created manually to record payment for a bill.',
    });
  }
  return cells;
}

export default TransactionPane;
